import React from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, Shield, Zap, FileText, Clock, TrendingUp, Users, Briefcase,
  Home, Heart, Gift, RefreshCw, ArrowUpCircle, Lightbulb, Star, Phone, Mail,
  MapPin, AlertTriangle, Headphones
} from 'lucide-react';

const PersonalLoan = () => {
  return (
    <div className="bg-gray-50">
      <div className="mb-6">
        <div className="flex justify-center lg:justify-end">
          <img
            src="personalloan.jpeg"
            alt="Loan Application Form"
            className="max-w-full h-full"
          />
        </div>
      </div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-16 animate-fade-up animation-delay-600">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Personal Loan Types</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: Zap, title: "Instant Personal Loans", desc: "Approved within minutes with minimal documentation; usually offered to existing customers or those with a strong credit profile. Ideal for urgent cash needs." },
              { icon: Clock, title: "Short-term Personal Loans", desc: "Repayment period from a few days up to 12 months – perfect for bridging temporary cash flow gaps." },
              { icon: Star, title: "Pre-approved Personal Loans", desc: "Tailored offers based on your credit history, income, and employer profile. No additional documentation required." },
              { icon: Gift, title: "Consumer Durable Loans", desc: "Specifically for purchasing electronics, furniture, or appliances. The loan amount is converted into EMIs; may require a down payment or processing fee." },
              { icon: RefreshCw, title: "Personal Loan Balance Transfer", desc: "Transfer your outstanding loan from another lender to CashPro for lower interest rates, reduced EMIs, or better terms. Ensure overall savings outweigh transfer costs." },
              { icon: ArrowUpCircle, title: "Personal Loan Top Up", desc: "Existing borrowers with a consistent repayment record can avail additional funds over and above their current loan amount." }
            ].map((type, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 group">
                <type.icon className="w-10 h-10 text-green-600 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{type.title}</h3>
                <p className="text-gray-600">{type.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-16 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-8 animate-fade-up animation-delay-800">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">How we can help?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "As one of India's leading loan facilitators,  CashPro Financial Services offers an unbiased platform to compare and choose the best personal loan deals from top banks and NBFCs.",
              "We provide a personalized loan consulting experience – understanding your financial profile to recommend the most suitable options.",
              "Our experienced loan consultants guide you through the entire process, from eligibility check to disbursal.",
              "We ensure your documents are complete and accurate, making the application process smooth, efficient, and hassle‑free.",
              "We proactively handle any issues or queries that arise during the application, keeping you informed at every step."
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <Lightbulb className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-16 animate-fade-up animation-delay-1000">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Why  CashPro Financial Services?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              "Get the best offers and exclusive deals from our extensive network of partner banks and NBFCs.",
              "Compare personal loan offers, interest rates, and terms side‑by‑side – all in one place.",
              "Apply with the lender of your choice – completely free of any service charges.",
              "Lowest interest rates guaranteed – we negotiate on your behalf to get you the most competitive rates.",
              "Hassle‑free process with easy documentation – minimal paperwork and digital uploads.",
              "Free document pickup by the CashPro Financial Services team – we come to you for collecting physical documents if needed.",
              "Free expert guidance and advice from our dedicated team of loan specialists.",
              "We are not an agent of any bank – you retain full control and make the final decision.",
              "No hidden fees or charges for our services – we work purely on referral models from lenders.",
              "We understand that urgent needs require quick solutions – we ensure rapid processing and speedy disbursal."
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-all">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-16 bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-xl animate-fade-up animation-delay-1200">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-6 h-6 text-yellow-600" />
            <h2 className="text-2xl font-bold text-gray-800">Points To Be Considered Before Taking A Personal Loan</h2>
          </div>
          <ul className="space-y-2 text-gray-700">
            <li>• Share accurate information when filling out the application form. Banks and NBFCs process loans based on the details you provide; any discrepancy may lead to rejection or delays.</li>
            <li>• Processing fees, if applicable, are deducted from the sanctioned loan amount. Never pay any fees in cash or by cheque to any individual or intermediary for loan approval.</li>
            <li>• Compare multiple offers and deals, but only hand over your personal documents to the lender you finally choose. Be cautious about sharing sensitive information prematurely.</li>
            <li>• Read the loan agreement carefully, including the terms regarding prepayment, foreclosure, and any hidden charges.</li>
          </ul>
        </div>
        <div className="bg-white rounded-2xl p-8 shadow-lg text-center animate-fade-up animation-delay-1400">
          <h2 className="text-3xl font-bold text-green-700 mb-4">We are Here to Help You</h2>
          <p className="text-gray-600 mb-6">Our mission is to provide reliable, up‑to‑date information and personalized assistance to make your loan journey smooth and successful.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">APPLY FOR LOAN</p>
              <p className="text-gray-800 font-medium mt-1">Looking for a Loan?</p>
              <button className="mt-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2 rounded-full transition duration-300 shadow">
                Apply Now
              </button>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">CALL US AT</p>
              <a href="tel:+918887141052" className="text-lg font-bold text-green-600 hover:underline">
                +91 88871 41052
              </a>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">MAIL US AT</p>
              <a href="mailto:info@cashpro.co.in" className="text-lg font-bold text-green-600 hover:underline">
                info@cashpro.co.in
              </a>
            </div>
          </div>
          <Link to="/contact" className="inline-block text-green-600 hover:underline font-medium">
            Contact Us →
          </Link>
        </div>
      </div>
      <style>{`
        @keyframes fade-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-up {
          animation: fade-up 0.6s ease-out forwards;
        }
        .animation-delay-200 {
          animation-delay: 0.2s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-400 {
          animation-delay: 0.4s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-600 {
          animation-delay: 0.6s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-800 {
          animation-delay: 0.8s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-1000 {
          animation-delay: 1s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-1200 {
          animation-delay: 1.2s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
        .animation-delay-1400 {
          animation-delay: 1.4s;
          opacity: 0;
          animation-fill-mode: forwards;
        }
      `}</style>
    </div>
  );
};

export default PersonalLoan;